import { useEffect, useMemo, useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from "recharts";
import { Plus, Trash2, Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";
import { useLocation } from "@/lib/locationContext";
import { toast } from "sonner";

const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";

const PLATFORMS = [
  { value: "Google",      color: "#4285F4" },
  { value: "Yelp",        color: "#d32323" },
  { value: "TripAdvisor", color: "#34e0a1" },
] as const;

type PlatformValue = typeof PLATFORMS[number]["value"];

interface ReviewRow {
  id: string;
  platform: PlatformValue;
  score: number;
  review_count: number;
  entry_date: string;
}

function todayStr(): string {
  return new Date().toISOString().split("T")[0];
}

export default function ReviewScoreTracker() {
  const { user } = useAuth();
  const { activeLocation } = useLocation();

  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [date, setDate] = useState(todayStr());
  const [platform, setPlatform] = useState<PlatformValue>("Google");
  const [score, setScore] = useState("");
  const [count, setCount] = useState("");

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      let q = supabase
        .from("review_scores")
        .select("id, platform, score, review_count, entry_date")
        .eq("user_id", user.id)
        .order("entry_date", { ascending: true });
      if (activeLocation?.id) q = q.eq("location_id", activeLocation.id);
      const { data, error } = await q;
      if (cancelled) return;
      if (error) toast.error("Couldn't load reviews");
      else setRows((data ?? []) as ReviewRow[]);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [user, activeLocation?.id]);

  // Build trend: one row per date, one column per platform.
  const trend = useMemo(() => {
    const byDate = new Map<string, Record<string, number | string>>();
    rows.forEach(r => {
      const cur = byDate.get(r.entry_date) ?? { date: r.entry_date };
      cur[r.platform] = Number(r.score);
      byDate.set(r.entry_date, cur);
    });
    return Array.from(byDate.values()).sort((a, b) =>
      String(a.date).localeCompare(String(b.date)),
    );
  }, [rows]);

  // Latest score per platform → average across platforms.
  const latestPerPlatform = useMemo(() => {
    const out: Record<string, ReviewRow> = {};
    rows.forEach(r => {
      const cur = out[r.platform];
      if (!cur || r.entry_date > cur.entry_date) out[r.platform] = r;
    });
    return out;
  }, [rows]);

  const overallAvg = useMemo(() => {
    const list = Object.values(latestPerPlatform);
    if (list.length === 0) return 0;
    return list.reduce((s, r) => s + Number(r.score), 0) / list.length;
  }, [latestPerPlatform]);

  const handleSave = async () => {
    if (!user) return;
    const s = Number(score);
    const c = count ? Number(count) : 0;
    if (!date || s < 1 || s > 5) {
      toast.error("Score must be between 1 and 5");
      return;
    }
    setSaving(true);
    const { data, error } = await supabase
      .from("review_scores")
      .insert({
        user_id: user.id,
        location_id: activeLocation?.id ?? null,
        entry_date: date,
        platform,
        score: s,
        review_count: c,
      })
      .select("id, platform, score, review_count, entry_date")
      .single();
    setSaving(false);
    if (error) { toast.error("Couldn't save"); return; }
    setRows(prev => [...prev, data as ReviewRow].sort((a, b) =>
      a.entry_date.localeCompare(b.entry_date),
    ));
    setScore(""); setCount("");
    toast.success("Logged");
  };

  const handleDelete = async (id: string) => {
    const prev = rows;
    setRows(rows.filter(r => r.id !== id));
    const { error } = await supabase.from("review_scores").delete().eq("id", id);
    if (error) {
      setRows(prev);
      toast.error("Couldn't delete");
    }
  };

  const overallTone =
    overallAvg >= 4.5 ? BRAND_GREEN :
    overallAvg >= 3.5 ? BRAND_ORANGE :
    overallAvg > 0 ? "#c0392b" : "#9ca3af";

  return (
    <div className="rounded-2xl border border-border bg-card p-4 sm:p-6 shadow-sm">
      <div className="flex items-center gap-3 mb-5">
        <div
          className="w-9 h-9 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${BRAND_ORANGE}1a` }}
        >
          <Star className="w-4 h-4" style={{ color: BRAND_ORANGE }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-heading text-lg font-medium" style={{ color: BRAND_DARK }}>
            Review Score Tracker
          </h2>
          <p className="text-xs text-muted-foreground">
            Log scores from Google, Yelp, and TripAdvisor over time
          </p>
        </div>
        <div className="text-right hidden sm:block">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Average</div>
          <div className="text-lg font-medium tabular-nums" style={{ color: overallTone }}>
            {overallAvg > 0 ? overallAvg.toFixed(2) : "—"}
            <span className="text-xs text-muted-foreground ml-1">/ 5</span>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-5">
        <div>
          <Label className="text-xs">Date</Label>
          <Input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Platform</Label>
          <Select value={platform} onValueChange={(v) => setPlatform(v as PlatformValue)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PLATFORMS.map(p => <SelectItem key={p.value} value={p.value}>{p.value}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Score (1–5)</Label>
          <Input type="number" min="1" max="5" step="0.1" value={score} onChange={e => setScore(e.target.value)} placeholder="4.5" />
        </div>
        <div>
          <Label className="text-xs"># reviews</Label>
          <Input type="number" min="0" value={count} onChange={e => setCount(e.target.value)} placeholder="0" />
        </div>
        <div className="col-span-2 lg:col-span-1 flex items-end">
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full gap-2"
            style={{ backgroundColor: BRAND_GREEN, color: "white" }}
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
            Log
          </Button>
        </div>
      </div>

      {/* Per-platform pills */}
      {Object.keys(latestPerPlatform).length > 0 && (
        <div className="flex flex-wrap gap-2 mb-5">
          {PLATFORMS.map(p => {
            const r = latestPerPlatform[p.value];
            if (!r) return null;
            return (
              <div
                key={p.value}
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border border-border bg-background"
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color }} />
                <span className="font-medium" style={{ color: BRAND_DARK }}>{p.value}</span>
                <span className="tabular-nums text-muted-foreground">
                  {Number(r.score).toFixed(1)} ★ · {r.review_count} reviews
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Trend chart */}
      <div className="mb-5">
        <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
          Score trend
        </div>
        {trend.length === 0 ? (
          <p className="text-sm text-muted-foreground italic py-4">
            Log scores above to see the trend.
          </p>
        ) : (
          <div className="h-56 w-full">
            <ResponsiveContainer>
              <LineChart data={trend} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#666" }} axisLine={false} tickLine={false} />
                <YAxis domain={[1, 5]} tick={{ fontSize: 11, fill: "#666" }} axisLine={false} tickLine={false} width={28} />
                <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #eee", fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                {PLATFORMS.map(p => (
                  <Line
                    key={p.value}
                    type="monotone"
                    dataKey={p.value}
                    stroke={p.color}
                    strokeWidth={2}
                    dot={{ r: 3 }}
                    connectNulls
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Recent log */}
      {!loading && rows.length > 0 && (
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-medium mb-2">
            Recent entries
          </div>
          <ul className="divide-y divide-border">
            {[...rows].reverse().slice(0, 6).map(r => (
              <li key={r.id} className="flex items-center justify-between py-2 text-sm">
                <div className="flex items-center gap-3">
                  <span className="tabular-nums text-muted-foreground w-24">{r.entry_date}</span>
                  <span className="font-medium" style={{ color: BRAND_DARK }}>{r.platform}</span>
                </div>
                <div className="flex items-center gap-3">
                  <span className="tabular-nums" style={{ color: BRAND_DARK }}>
                    {Number(r.score).toFixed(1)} ★
                  </span>
                  <span className="tabular-nums text-xs text-muted-foreground hidden sm:inline">
                    {r.review_count} reviews
                  </span>
                  <Button variant="ghost" size="icon" onClick={() => handleDelete(r.id)} aria-label="Delete">
                    <Trash2 className="w-4 h-4 text-muted-foreground" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}